import React, { useState, useEffect, useRef, useReducer } from "react";
import apiService from "../../utils/apiService";
import { API_BASE_URL } from "../../utils/apiConfig";
import { formatNumber } from "../../utils/formatNumber";

const Balance = ({ dashboardData, setDashboardData }) => {
  const [autoExchange, setAutoExchange] = useState(true);
  const [autoFees, setAutoFees] = useState(true);
  const [filterValue, setFilterValue] = useState("today");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [availableBalance, setAvailableBalance] = useState(null);
  // console.log("bfhgfr5588885kwhgrkhg", availableBalance)
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState(null);
  const dropdownRef = useRef(null);
  const openTronScan = () => {
    const trc20Address = getTRC20WalletAddress();
    if (trc20Address) {
      const url = `https://tronscan.org/#/address/${trc20Address}`;
      window.open(url, "_blank");
    } else {
      // Show alert if no wallet address is available
      alert("TRC20 wallet address not available. Please try again later.");
    }
  };

  const openEtherscan = () => {
    const erc20Address = getERC20WalletAddress();
    if (erc20Address) {
      const url = `https://etherscan.io/address/${erc20Address}`;
      window.open(url, "_blank");
    } else {
    }
  };

  const getTRC20WalletAddress = () => {
    if (
      !dashboardData ||
      !dashboardData.wallet ||
      !Array.isArray(dashboardData.wallet)
    )
      return null;

    const trc20Wallet = dashboardData.wallet.find(
      (wallet) => wallet.walletNetwork === "USDT-TRC20",
    );

    return trc20Wallet ? trc20Wallet.walletAddress : null;
  };

  const getERC20WalletAddress = () => {
    if (!dashboardData || !Array.isArray(dashboardData.wallet)) return null;

    const erc20Wallet = dashboardData.wallet.find(
      (wallet) => wallet.walletNetwork === "USDT-ERC20",
    );

    return erc20Wallet ? erc20Wallet.walletAddress : null;
  };

  // Filter options
  const filterOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "thisWeek", label: "This Week" },
    { value: "thisMonth", label: "This Month" },
    { value: "last3Months", label: "Last 3 Months" },
    { value: "last6Months", label: "Last 6 Months" },
    { value: "thisYear", label: "This Year" },
    { value: "customRange", label: "Custom Range" },
  ];

  // Fetch dashboard data from API
  const fetchDashboardData = async (
    filter = "last3Months",
    customRange = null,
  ) => {
    setLoading(true);
    setError(null);

    try {
      let url = `${API_BASE_URL}/dashboard/merchant?filter=${filter}`;

      // Add custom date range parameters if provided
      if (
        filter === "customRange" &&
        customRange &&
        customRange.from &&
        customRange.to
      ) {
        url += `&startDate=${customRange.from}&endDate=${customRange.to}`;
      }

      const response = await fetch(url, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setDashboardData(data.data);
      } else {
        setDashboardData(data);
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);

      // Provide more user-friendly error messages
      let errorMessage = "Failed to fetch dashboard data";
      if (err.message.includes("401")) {
        errorMessage = "Authentication required. Please log in again.";
      } else if (err.message.includes("403")) {
        errorMessage =
          "Access denied. You don't have permission to view dashboard data.";
      } else if (err.message.includes("404")) {
        errorMessage = "Dashboard endpoint not found. Please contact support.";
      } else if (err.message.includes("500")) {
        errorMessage = "Server error. Please try again later.";
      } else if (
        err.message.includes("NetworkError") ||
        err.message.includes("fetch")
      ) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch data on component mount and when filter changes
  useEffect(() => {
    if (filterValue === "customRange") {
      // Don't fetch automatically for custom range, wait for date selection
      return;
    }
    fetchDashboardData(filterValue);
  }, [filterValue]);

  // Handle custom date range submission
  const handleCustomDateSubmit = () => {
    if (customDateRange.from && customDateRange.to) {
      fetchDashboardData("customRange", customDateRange);
      setShowDatePicker(false);
    }
  };

  // Calculate dynamic percentages for the bar chart (Three segments only)
  const calculateBarPercentages = () => {
    if (!dashboardData) {
      return { totalPercentage: 0, feePercentage: 0, availablePercentage: 0 };
    }

    const totalAmount = dashboardData.totalAmount || 0;
    const availableBalance = dashboardData.availableBalance || 0;
    const feeAmount = dashboardData.feeAmt || 0;

    // Calculate the sum of all three values
    const sumOfAll = totalAmount + availableBalance + feeAmount;

    // If sum is 0, return equal distribution or 0
    if (sumOfAll === 0) {
      return {
        totalPercentage: 33.33,
        feePercentage: 33.33,
        availablePercentage: 33.34,
      };
    }

    // Calculate percentages based on the sum of all values
    const totalPercentage = (totalAmount / sumOfAll) * 100;
    const feePercentage = (feeAmount / sumOfAll) * 100;
    const availablePercentage = (availableBalance / sumOfAll) * 100;

    return {
      totalPercentage: Math.max(0, Math.min(100, totalPercentage)),
      feePercentage: Math.max(0, Math.min(100, feePercentage)),
      availablePercentage: Math.max(0, Math.min(100, availablePercentage)),
    };
  };

  const { totalPercentage, feePercentage, availablePercentage } =
    calculateBarPercentages();

  const total = feePercentage + availablePercentage;

  const feePercentage1 = (feePercentage / total) * 100;
  const availablePercentage1 = (availablePercentage / total) * 100;
  // Fetch user wallet data from API
  const fetchUserWallet = async () => {
    setWalletLoading(true);
    setWalletError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/user-wallet`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // console.log("User Wallet Response:", data);

      if (data.success) {
        setWalletData(data.data.wallets);
        setAvailableBalance(data?.data);
      } else {
        setWalletData(data);
      }
    } catch (err) {
      console.error("Error fetching user wallet:", err);
      setWalletError(err.message || "Failed to fetch wallet data");
    } finally {
      setWalletLoading(false);
    }
  };

  // Fetch wallet data on component mount
  useEffect(() => {
    fetchUserWallet();
  }, []);

  const erc20Fee =
    walletData?.find((w) => w.walletNetwork === "USDT-ERC20")?.feeAmount || 0;
  const trc20Fee =
    walletData?.find((w) => w.walletNetwork === "USDT-TRC20")?.feeAmount || 0;

  const totalFee = erc20Fee + trc20Fee;
  return (
    <div className="relative flex flex-col xl:flex-row gap-4 py-3 bg-gray-100">
      {/* Balance Summary Card */}
      <div className="bg-white rounded-[25px] p-4 lg:p-6 shadow-sm flex-1 xl:flex-[2]">
        {/* Header Section - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
          <h2 className="text-lg font-semibold text-gray-900">
            Balance summary
          </h2>

          {/* Action Buttons - Mobile Responsive */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            {/* Wallet Buttons - Stack on mobile, inline on larger screens */}
            {/* <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={openTronScan}
                className="flex items-center justify-center space-x-2 px-3 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors text-sm"
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
                <span className="font-medium">TRC20</span>
              </button>
              <button
                onClick={openEtherscan}
                className="flex items-center justify-center space-x-2 px-3 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors text-sm"
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
                <span className="font-medium">ERC20</span>
              </button>
            </div> */}

            {/* Filter Dropdown */}
            <div className="relative" ref={dropdownRef}>
              {/* <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center justify-center space-x-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors w-full sm:w-auto"
              >
                <span className="text-sm text-gray-700">
                  {filterOptions.find((option) => option.value === filterValue)
                    ?.label || "Filter"}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${isFilterOpen ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button> */}

              {/* Dropdown Menu - Mobile Responsive */}
              {isFilterOpen && (
                <div className="absolute right-0 sm:right-0 left-0 sm:left-auto mt-2 w-full sm:w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <div className="py-1">
                    {filterOptions?.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setFilterValue(option.value);
                          setIsFilterOpen(false);
                          if (option.value === "customRange") {
                            setShowDatePicker(true);
                          }
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          filterValue === option.value
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Custom Date Range Picker - Mobile Responsive */}
          {/* {showDatePicker && (
            <div className="absolute right-0 sm:right-0 left-0 sm:left-auto mt-2 w-full sm:w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-4">
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-900">Select Date Range</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      From Date
                    </label>
                    <input
                      type="date"
                      value={customDateRange.from}
                      onChange={(e) => setCustomDateRange(prev => ({ ...prev, from: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      To Date
                    </label>
                    <input
                      type="date"
                      value={customDateRange.to}
                      onChange={(e) => setCustomDateRange(prev => ({ ...prev, to: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <button
                    onClick={handleCustomDateSubmit}
                    disabled={!customDateRange.from || !customDateRange.to}
                    className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => {
                      setShowDatePicker(false);
                      setCustomDateRange({ from: "", to: "" });
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )} */}
        </div>

        {/* Horizontal Stacked Bar Chart - Dynamic (Three Colors Only) */}
        <div className="w-full bg-gray-200 rounded-md h-9 mb-4 overflow-hidden">
          <div className="h-full flex">
            {/* Total Amount - Blue segment */}
            {/* {totalPercentage > 0 && (
              <div
                className="bg-blue-500 flex-shrink-0 transition-all duration-300"
                style={{ width: `${totalPercentage}%` }}
                title={`Total Amount: ${formatNumber(totalPercentage, 2)}%`}
              ></div>
            )} */}
            {/* Fees - Light Blue segment */}
            {feePercentage1 > 0 && (
              <div
                className="bg-[#4FC3F7] flex-shrink-0 transition-all duration-300"
                style={{ width: `${feePercentage1}%` }}
                title={`Fees: ${formatNumber(feePercentage1, 2)}%`}
              ></div>
            )}
            {/* Available Balance - Teal segment */}
            {availablePercentage1 > 0 && (
              <div
                className="bg-blue-500 flex-shrink-0 transition-all duration-300"
                style={{ width: `${availablePercentage1}%` }}
                title={`Available Balance: ${formatNumber(availablePercentage1, 2)}%`}
              ></div>
            )}
          </div>
        </div>
        {/* Loading State - Skeleton */}
        {/* {loading && (
          <div className="space-y-0">
            <div className="flex justify-between items-center py-3 border-b border-gray-200">
              <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
            </div>

            {[1, 2, 3].map((index) => (
              <div key={index} className="flex justify-between items-center py-3 lg:py-4 border-b border-gray-200">
                <div className="flex items-center space-x-2 lg:space-x-3">
                  <div className="w-4 h-4 bg-gray-200 rounded-sm animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
              </div>
            ))}
          </div>
        )} */}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-red-400 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-red-700 text-sm">{error}</span>
            </div>
            <button
              onClick={() => fetchDashboardData(filterValue)}
              className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Data Table - Mobile Responsive with Row Layout */}
        {/* {!loading && !error && dashboardData && ( */}
        <div className="space-y-0">
          {/* Header - Hidden on mobile, shown on larger screens */}
          <div className="hidden sm:flex justify-between items-center py-3 border-b border-gray-200">
            <span className="font-semibold text-gray-900 text-sm lg:text-base">
              Balance Type
            </span>
            <span className="font-semibold text-gray-900 text-sm lg:text-base">
              Amount
            </span>
          </div>

          {/* Total Amount Row - Always Row Layout on Small Devices */}
          {/* <div className="flex justify-between items-center py-4 sm:py-3 lg:py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2 lg:space-x-3">
              <div className="w-4 h-4 bg-blue-500 rounded-sm flex-shrink-0"></div>
              <span className="text-gray-900 text-sm lg:text-base font-medium sm:font-normal">
                Total Amountz
              </span>
            </div>
            <span className="text-gray-900 font-semibold text-sm sm:text-base lg:text-base">
              {((availableBalance?.availableBalance || 0) + totalFee).toFixed(2)} USDT
            </span>
          </div>

          {/* ERC20 Amount Row - Always Row Layout on Small Devices */}
          {/* <div className="flex justify-between items-center py-4 sm:py-3 lg:py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2 lg:space-x-3">
              <div className="w-4 h-4 bg-green-500 rounded-sm flex-shrink-0"></div>
              <span className="text-gray-900 text-sm lg:text-base font-medium sm:font-normal">
                ERC20 Amount
              </span>
            </div>
            <span className="text-gray-900 font-semibold text-sm sm:text-base lg:text-base">
              ${walletData?.find(w => w.walletNetwork === "USDT-ERC20")?.balance?.toFixed(2) || "0.00"} USDT
            </span>
          </div> */}

          {/* TRC20 Amount Row - Always Row Layout on Small Devices */}
          {/* <div className="flex justify-between items-center py-4 sm:py-3 lg:py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2 lg:space-x-3">
              <div className="w-4 h-4 bg-gray-600 rounded-sm flex-shrink-0"></div>
              <span className="text-gray-900 text-sm lg:text-base font-medium sm:font-normal">
                TRC20 Amount
              </span>
            </div>
            <span className="text-gray-900 font-semibold text-sm sm:text-base lg:text-base">
              ${walletData?.find(w => w.walletNetwork === "USDT-TRC20")?.balance?.toFixed(2) || "0.00"} USDT
            </span>
          </div> */}

          {/* Fee Amount Row - Always Row Layout on Small Devices */}
          <div className="flex justify-between items-center py-4 sm:py-3 lg:py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2 lg:space-x-3">
              <div className="w-4 h-4 bg-[#4FC3F7] rounded-sm flex-shrink-0"></div>
              <span className="text-gray-900 text-sm lg:text-base font-medium sm:font-normal">
                Fee Amount
              </span>
            </div>
            <span className="text-gray-900 font-semibold text-sm sm:text-base lg:text-base">
              {formatNumber(totalFee, 2)} USDT
            </span>
          </div>

          {/* Available Balance Row - Always Row Layout on Small Devices */}
          <div className="flex justify-between items-center py-4 sm:py-3 lg:py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2 lg:space-x-3">
              <div className="w-4 h-4 bg-blue-500 rounded-sm flex-shrink-0"></div>
              <span className="text-gray-900 text-sm lg:text-base font-medium sm:font-normal">
                Available Balance
              </span>
            </div>
            <span className="text-gray-900 font-semibold text-sm sm:text-base lg:text-base">
              {formatNumber(availableBalance?.availableBalance || 0, 2)} USDT
            </span>
          </div>
        </div>
        {/* )} */}

        {/* Fallback when no data */}
        {!loading && !error && !dashboardData && (
          <div className="text-center py-8 text-gray-500">
            <p>No data available</p>
          </div>
        )}
      </div>

      {/* Payment Flows Card - Mobile Responsive */}
      <div className="bg-white rounded-[15px] p-4 lg:p-6 shadow-sm flex-1 xl:flex-[1]">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Payment Flows
        </h2>
        <p className="text-gray-900 mb-4 lg:mb-6 text-sm lg:text-base">
          Exchange
        </p>

        {/* Auto-exchange Option - Mobile Optimized */}

        {/* Auto-Fees Option - Mobile Optimized */}
        <div className="bg-gray-50 rounded-lg p-3 lg:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img
                  src="dollar.png"
                  alt="Auto-fees"
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="flex flex-col">
                <span className="text-gray-900 font-medium text-sm lg:text-base">
                  Auto-Fees
                </span>
                <span className="text-xs text-gray-500 hidden sm:block">
                  Automatically handle transaction fees
                </span>
              </div>
            </div>
            <button
              // onClick={() => setAutoFees(!autoFees)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                autoFees ? "bg-gray-200" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full transition-transform ${
                  autoFees
                    ? "translate-x-5 bg-[#00528A]" // circle color when active
                    : "translate-x-1 bg-gray-400" // circle color when inactive
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Balance;
